import React from 'react';
import { connect } from 'react-redux';
import { localize } from 'react-localize-redux';
import MediaQuery from 'react-responsive';
import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';

import { createMuiTheme } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import { MuiThemeProvider } from '@material-ui/core';
import { browserHistory } from 'react-router';
import camelCase from 'lodash/camelCase';

import { AccentColor } from 'helpers/AccentColorHelper';
import { featureEnabled } from 'helpers/ToggleBot';
import { normalizePhone } from 'helpers/Formatter';
import { toFriendlyDateString } from 'helpers/Formatter';
import { drawerStyle } from 'helpers/tableHelpers';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import { hostIsBoardwalk } from 'helpers/CurrentWindowLocation';

import { RenderIfToggleOn, RenderIfToggleOff } from 'components/shared/ToggleRenderer';
import { default as AntDrawer } from 'components/ui/Drawer';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import TableFilters from 'components/ui/TableFilters';
import StatusTag from 'components/ui/StatusTag';
import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import NoticeDetailsOverview from './NoticeDetailsOverview';
import { getProperties } from 'actions/landlordPropertiesActions';
import * as landlordNoticesActions from 'actions/landlordNoticesActions';

import AddNoticeDialog from './AddNoticeDialog';

import CreateNotice from './AddNoticeDrawer/CreateNotice';
import EditNotice from './AddNoticeDrawer/EditNotice';
import ManageTemplates from './AddNoticeTemplateDrawer/ManageTemplates';

const statuses = ['Delivered', 'Partially Delivered', 'Queued For Delivery', 'Scheduled'];

class LandlordNotices extends React.Component {
  state = {
    showAddNoticeDialog: false,
    showAddNoticeTemplate: false,
    disabledAddNoticeSubmit: false,
    showNoticeDrawer: false,
    noticeDetails: null,
    filtersActive: false,
    currentTab: 'all',
    loadingInitial: true,
    filters: {
      search: '',
      properties: Array(this.props.properties.length).fill(false),
      statuses: Array(statuses.length).fill(false),
    },
    sortKey: '',
    sortOrder: '',
    offset: 0,
  };

  setRef = (ref) => (this.table = ref);

  fetchNoticesAndStats = () => {
    this.props.getNoticesStats();
    this.props.getNoticeTemplates();
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchNotices()
    );
    this.setState({ loadingInitial: false });
  };

  componentDidMount = () => {
    const { noticeId } = this.props.routeParams;

    this.fetchNoticesAndStats();
    this.props.getProperties({ for_multiselect_floors_risers: true });

    if (noticeId) {
      this.toggleDrawer(noticeId);
    }
  };

  toggleDrawer = async (noticeId) => {
    if (!this.state.showNoticeDrawer && noticeId) {
      await this.props.getNoticeDetails(noticeId);
      await this.props.getNoticeDeliveryReport(noticeId);
      if (!this.props.noticeDetails || !this.props.noticeDetails.id) {
        browserHistory.push('/companies/notices');
        return;
      }
      this.setState({
        showNoticeDrawer: true,
      });
      browserHistory.push(`/companies/notices/${noticeId}`);
      return;
    }
    browserHistory.push('/companies/notices');
    this.setState({
      showNoticeDrawer: false,
    });
  };

  // Methods for the dialog
  toggleAddNotice = () => {
    this.setState({
      showAddNoticeDialog: !this.state.showAddNoticeDialog,
    });
  };

  toggleAddNoticeTemplate = () => {
    this.setState({
      showAddNoticeTemplate: !this.state.showAddNoticeTemplate,
    });
  };

  submitCreateNoticeForm = (formData) => {
    this.setState({ disabledAddNoticeSubmit: true });
    this.props.createOrUpdateNotice(null, formData).then(() => {
      this.toggleAddNotice();
      this.setState(
        {
          disabledAddNoticeSubmit: false,
          offset: 0,
        },
        () => this.fetchNotices()
      );
    });
  };

  submitTemplateForm = (formData) => {
    let requestPromise = null;
    if (formData.hasOwnProperty('id')) {
      requestPromise = this.props.putNoticeTemplate(formData.id, formData);
    } else {
      requestPromise = this.props.postNoticeTemplate(formData);
    }

    return requestPromise.then(() => {
      this.props.getNoticeTemplates();
    });
  };

  deleteNoticeTemplate = (noticeTemplateId) => {
    return this.props.deleteNoticeTemplate(noticeTemplateId).then(() => {
      this.props.getNoticeTemplates();
    });
  };

  // Methods for drawer
  openNoticeInDrawer = async (noticeId) => {
    await this.props.getNoticeDetails(noticeId);
    await this.props.getNoticeDeliveryReport(noticeId);

    this.toggleDrawer();
  };

  updateNotice = (formData) => {
    const noticeId = this.props.noticeDetails.id;
    this.props.createOrUpdateNotice(noticeId, formData).then(() => {
      this.setState(
        {
          offset: 0,
        },
        () => this.fetchNotices()
      );
    });
  };

  deleteNotice = () => {
    this.props.deleteNotice(this.props.noticeDetails.id).then(() => {
      this.setState({ showConfirmation: false });
      this.toggleDrawer();
      this.setState(
        {
          offset: 0,
        },
        () => this.fetchNotices()
      );
    });
  };

  filterProperties = (booleanArrayMask) => {
    return this.props.properties.filter((_, i) => booleanArrayMask[i]);
  };

  filterStatuses = (booleanArrayMask) => {
    return statuses.filter((_, i) => booleanArrayMask[i]);
  };

  handleClearSearch = (setFieldValue) => {
    this.setState(
      {
        filtersActive: false,
        filters: {
          search: '',
          properties: Array(this.props.properties.length).fill(false),
          statuses: Array(statuses.length).fill(false),
        },
        offset: 0,
      },
      () => this.fetchNotices()
    );

    if (this.table) this.table.scrollToTop(0);
    setFieldValue('search', '');
    setFieldValue('properties', Array(this.props.properties.length).fill(false));
    setFieldValue('statuses', Array(statuses.length).fill(false));
  };

  handleFilters = (values) => {
    const isFiltersUntouched =
      values.search === '' && !values.properties.includes(true) && !values.statuses.includes(true);
    const newState = {
      filtersActive: !isFiltersUntouched,
      filters: {
        search: values.search,
        properties: values.properties,
        statuses: values.statuses,
      },
      drawerVisible: false,
      offset: 0,
    };

    if (this.table) this.table.scrollToTop(0);
    this.setState(newState, () => this.fetchNotices());
  };

  handleTabChange = (tabName) => {
    this.setState(
      {
        currentTab: tabName,
        offset: 0,
      },
      () => {
        this.fetchNotices();
      }
    );
    if (this.table) this.table.scrollToTop(0);
  };

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => {
        this.fetchNotices();
      }
    );

    if (this.table) this.table.scrollToTop(0);
  };

  fetchNotices = () => {
    const { filters, filtersActive, currentTab, offset, sortKey, sortOrder } = this.state;

    let fetchPromise = null;
    let filterOptions = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };
    let subset = currentTab;

    if (filtersActive) {
      const propertiesToFilter = this.filterProperties(filters.properties).map(
        (data) => data.value
      );
      const statusesToFilter = this.filterStatuses(filters.statuses);
      filterOptions = {
        query: filters.search,
        property_ids: propertiesToFilter,
        statuses: statusesToFilter,
        sort_key: sortKey,
        sort_order: sortOrder,
      };

      subset = 'all';
    }

    fetchPromise = this.props.getFilteredNotices(subset, offset, filterOptions);

    fetchPromise.then(() => {
      this.setState({
        offset: this.props.notices.length,
      });
    });
  };

  columns = [
    {
      title: getTranslatedString('landlordPortal.landlordNotices.status'),
      dataKey: 'status',
      sortable: false,
      width: 175,
      render: ({ data }) => <StatusTag status={data} />,
    },
    {
      title: getTranslatedString('landlordPortal.landlordNotices.name'),
      dataKey: 'name',
      sortable: true,
      width: 200,
    },
    {
      title: getTranslatedString('landlordPortal.landlordNotices.description'),
      dataKey: 'description',
      sortable: false,
      width: 200,
    },
    {
      title: getTranslatedString('landlordPortal.landlordNotices.propertiesAssigned'),
      dataKey: 'properties',
      sortable: false,
      width: 175,
      render: ({ data }) => <span>{data.length === 0 ? 1 : data.length}</span>,
    },
    {
      title: getTranslatedString('landlordPortal.landlordNotices.unitsAssigned'),
      dataKey: 'units',
      sortable: false,
      width: 175,
      render: ({ data }) => (
        <span>
          {data.length === 0
            ? `${getTranslatedString('landlordPortal.landlordNotices.all')}`
            : data.length}
        </span>
      ),
    },
    {
      title: getTranslatedString('landlordPortal.landlordNotices.postDate'),
      dataKey: 'post_date',
      sortable: true,
      width: 175,
      render: ({ data }) => {
        return toFriendlyDateString(data, {
          language: this.props.currentLanguage,
        });
      },
    },
    {
      title: getTranslatedString('landlordPortal.landlordNotices.expirationDate'),
      dataKey: 'expiration_date',
      sortable: true,
      width: 175,
      render: ({ data }) => {
        return toFriendlyDateString(data, {
          language: this.props.currentLanguage,
        });
      },
    },
  ];

  render() {
    const { filters } = this.state;
    const { notices, noticesCounts, noticeTemplateManager } = this.props;
    const filterOptions = [
      {
        useSearch: true,
        options: this.props.properties.map((data) => data.label),
        name: 'properties',
        title: getTranslatedString('landlordPortal.landlordNotices.selectPropertiesToFilter'),
      },
      {
        useSearch: false,
        options: statuses.map((statusCode) =>
          getTranslatedString(`landlordPortal.landlordNotices.${camelCase(statusCode)}`)
        ),
        name: 'statuses',
        title: getTranslatedString('landlordPortal.landlordNotices.filterByNoticeStatus'),
      },
    ];

    const tabs = [
      {
        title: getTranslatedString('landlordPortal.landlordNotices.all'),
        label: getTranslatedString('landlordPortal.landlordNotices.all'),
        value: 'all',
        count: noticesCounts['all'] || 0,
        onClick: () => this.handleTabChange('all'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordNotices.delivered'),
        label: getTranslatedString('landlordPortal.landlordNotices.delivered'),
        value: 'delivered',
        count: noticesCounts['delivered'] || 0,
        onClick: () => this.handleTabChange('delivered'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordNotices.scheduled'),
        label: getTranslatedString('landlordPortal.landlordNotices.scheduled'),
        value: 'scheduled',
        count: noticesCounts['scheduled'] || 0,
        onClick: () => this.handleTabChange('scheduled'),
      },
      {
        title: getTranslatedString('landlordPortal.landlordNotices.expired'),
        label: getTranslatedString('landlordPortal.landlordNotices.expired'),
        value: 'expired',
        count: noticesCounts['expired'] || 0,
        onClick: () => this.handleTabChange('expired'),
      },
    ];

    const filteredData = notices.map((datum) => {
      if (datum.phone_number) {
        return {
          ...datum,
          phone_number: normalizePhone(datum.phone_number),
        };
      } else {
        return datum;
      }
    });

    const canCreateNotice = !hostIsBoardwalk() || this.props.admin;
    return (
      <StyledMain>
        <StyledHeaderContainer>
          <StyledHeader>
            <HeaderWithPeriod
              headerText={getTranslatedString('landlordPortal.landlordNotices.notices')}
              newStyle
            />
            {canCreateNotice && (
              <StyledAddButton accentColor={AccentColor()} onClick={this.toggleAddNotice}>
                {getTranslatedString('landlordPortal.landlordNotices.addNew')}
              </StyledAddButton>
            )}
            {canCreateNotice && noticeTemplateManager && featureEnabled('ui.notices.new_drawer') && (
              <StyledAddButton accentColor={AccentColor()} onClick={this.toggleAddNoticeTemplate}>
                {getTranslatedString('landlordPortal.addNoticeDialog.manageTemplates')}
              </StyledAddButton>
            )}
          </StyledHeader>
          <TableFilters
            filters={filters}
            handleSubmitFilters={this.handleFilters}
            handleClearFilters={this.handleClearSearch}
            filterOptions={filterOptions}
            accentColor={AccentColor()}
          />
        </StyledHeaderContainer>

        <StyledTableContainer>
          {this._renderTableView({ filteredData, tabs })}
          {this._renderMobileView({
            filteredData,
            tabs,
            currentTab: this.state.currentTab,
            tabChange: this.handleTabChange,
          })}
        </StyledTableContainer>
        {this.renderAddNotice()}
        {this.renderAddNoticeTemplate()}
        {this.renderNoticeDrawer()}
      </StyledMain>
    );
  }

  renderAddNoticeTemplate = () => {
    const { noticeTemplateManager } = this.props;
    if (!noticeTemplateManager) return null;

    // The AddNoticeDialog renders just the template manager
    // if the 'ui.notices.new_drawer' feature is enabled.
    return (
      <RenderIfToggleOn toggle="ui.notices.new_drawer">
        <AntDrawer
          maskClosable={false}
          keyboard
          visible={this.state.showAddNoticeTemplate}
          onClose={this.toggleAddNoticeTemplate}
          closable={false}
          width={600}
        >
          {this.state.showAddNoticeTemplate && (
            <ManageTemplates
              handleClose={this.toggleAddNoticeTemplate}
              onSubmissionComplete={() => this.fetchNoticesAndStats()}
            />
          )}
        </AntDrawer>
      </RenderIfToggleOn>
    );
  };

  renderAddNotice = () => {
    return (
      <>
        <RenderIfToggleOff toggle="ui.notices.new_drawer">
          <AddNoticeDialog
            open={this.state.showAddNoticeDialog}
            toggleDialog={this.toggleAddNotice}
            disabledAddNoticeSubmit={this.state.disabledAddNoticeSubmit}
            properties={this.props.properties}
            noticeTemplates={this.props.noticeTemplates}
            postNotice={this.submitCreateNoticeForm}
            submitTemplateForm={this.submitTemplateForm}
            deleteTemplate={this.deleteNoticeTemplate}
          />
        </RenderIfToggleOff>
        <RenderIfToggleOn toggle="ui.notices.new_drawer">
          <AntDrawer
            maskClosable={false}
            keyboard
            visible={this.state.showAddNoticeDialog}
            onClose={this.toggleAddNotice}
            closable={false}
            width={600}
          >
            {this.state.showAddNoticeDialog && (
              <CreateNotice
                handleClose={this.toggleAddNotice}
                onSubmissionComplete={() => this.fetchNoticesAndStats()}
              />
            )}
          </AntDrawer>
        </RenderIfToggleOn>
      </>
    );
  };

  _renderMobileView = ({ filteredData, tabs, currentTab, tabChange }) => (
    <MediaQuery query="(max-device-width: 750px)">
      <MobileTable
        data={filteredData}
        tabs={tabs}
        tabIndex={currentTab}
        tabChange={tabChange}
        columns={this.columns}
        title="name"
        tag="status"
        filtersActive={this.state.filtersActive}
        stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
        fieldsBelowFold={[
          {
            title: getTranslatedString('landlordPortal.landlordNotices.description'),
            key: 'description',
          },
        ]}
        fieldsAboveFold={[
          {
            title: getTranslatedString('landlordPortal.landlordNotices.postDate'),
            key: 'post_date',
          },
          {
            title: getTranslatedString('landlordPortal.landlordNotices.expirationDate'),
            key: 'expiration_date',
          },
        ]}
        onRowClick={(data) => {
          this.toggleDrawer(data.id);
        }}
      />
    </MediaQuery>
  );

  _renderTableView = ({ filteredData, tabs }) => (
    <MediaQuery query="(min-device-width: 750px)">
      <TabbedTable
        setRef={this.setRef}
        tabs={tabs}
        columns={this.columns}
        data={filteredData}
        loadMoreData={this.fetchNotices}
        isLoading={this.state.loadingInitial}
        filtersActive={this.state.filtersActive}
        onRowClick={(data) => {
          this.toggleDrawer(data.original_id);
        }}
        onColumnClick={(data) => {
          this.handleSort(data.key, data.order);
        }}
      />
    </MediaQuery>
  );

  renderNoticeDrawer = () => {
    let customDrawerStyle = { ...drawerStyle };
    customDrawerStyle.overrides.MuiDrawer.paper = {
      height: '100% !important',
      backgroundColor: colors.LIGHTEST_GRAY,
    };
    return (
      <>
        <RenderIfToggleOff toggle="ui.notices.new_drawer">
          <MuiThemeProvider theme={createMuiTheme(customDrawerStyle)}>
            <Drawer open={this.state.showNoticeDrawer} onClose={this.toggleDrawer} anchor="left">
              <NoticeDetailsOverview
                toggleDrawer={this.toggleDrawer}
                properties={this.props.properties}
                noticeTemplates={this.props.noticeTemplates}
                notice={this.props.noticeDetails}
                updateNotice={this.updateNotice}
                deleteNotice={this.deleteNotice}
                deliveryReport={this.props.deliveryReport}
                downloadTargetedPdf={this.props.downloadTargetedPdf}
                downloadGenericPdf={this.props.downloadGenericPdf}
                isNoticeDrawer={this.state.showNoticeDrawer}
              />
            </Drawer>
          </MuiThemeProvider>
        </RenderIfToggleOff>
        <RenderIfToggleOn toggle="ui.notices.new_drawer">
          <AntDrawer
            maskClosable={false}
            keyboard
            visible={this.state.showNoticeDrawer}
            onClose={this.toggleDrawer}
            closable={false}
            width={600}
          >
            {this.state.showNoticeDrawer && (
              <EditNotice
                handleClose={this.toggleDrawer}
                onSubmissionComplete={() => this.fetchNoticesAndStats()}
              />
            )}
          </AntDrawer>
        </RenderIfToggleOn>
      </>
    );
  };
}

export default connect(
  (store) => ({
    notices: store.landlordNotices.notices,
    noticesCounts: store.landlordNotices.noticesCounts,
    deliveryReport: store.landlordNotices.delivery_report,
    properties: store.landlordProperties.properties,
    noticeTemplates: store.landlordNotices.notice_templates,
    currentLanguage: store.locale.currentLanguage,
    noticeDetails: store.landlordNotices.noticeDetails,
    noticeTemplateManager: store.userTypes.noticeTemplateManager,
    admin: store.userTypes.admin,
  }),
  {
    ...landlordNoticesActions,
    getProperties,
  }
)(localize()(LandlordNotices));
